import type { ScopeRegistration } from '@src/services/shortcuts/types';

interface TimelineStageShortcutDeps {
  frameRate: number;
  getTime: () => number;
  getDuration: () => number;
  navigateToTime: (time: number) => Promise<void> | void;
  navigateToNextFrame: () => Promise<void> | void;
  navigateToPreviousFrame: () => Promise<void> | void;
  navigateToNextEditPoint: () => Promise<void> | void;
  navigateToPreviousEditPoint: () => Promise<void> | void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomToFit: () => void;
  splitActiveTrackElementAtCurrentTime: () => Promise<unknown> | unknown;
  deleteActiveElement: () => Promise<unknown> | unknown;
  copyActiveElement: () => unknown;
  pasteElement: () => unknown;
  enableCopyPaste?: boolean;
  isActive: () => boolean;
}

export function createTimelineStageShortcutScope(
  deps: TimelineStageShortcutDeps,
): ScopeRegistration {
  return {
    scopeId: 'timeline-stage',
    isActive: () => deps.isActive(),
    shortcuts: [
      {
        id: 'timeline.navigateToNextFrame',
        chord: { code: 'ArrowRight' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.navigateToNextFrame();
        },
      },
      {
        id: 'timeline.navigateToPreviousFrame',
        chord: { code: 'ArrowLeft' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.navigateToPreviousFrame();
        },
      },
      {
        id: 'timeline.navigateToNextEditPoint',
        chord: { code: 'ArrowUp' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.navigateToNextEditPoint();
        },
      },
      {
        id: 'timeline.navigateToPreviousEditPoint',
        chord: { code: 'ArrowDown' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.navigateToPreviousEditPoint();
        },
      },
      {
        id: 'timeline.navigateStart',
        chord: { code: 'Home' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.navigateToTime(0);
        },
      },
      {
        id: 'timeline.navigateEnd',
        chord: { code: 'End' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.navigateToTime(deps.getDuration());
        },
      },
      {
        id: 'timeline.zoomIn',
        chord: { code: 'Equal' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.zoomIn();
        },
      },
      {
        id: 'timeline.zoomOut',
        chord: { code: 'Minus' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.zoomOut();
        },
      },
      {
        id: 'timeline.zoomToFit',
        chord: { code: 'Slash' },
        priority: 50,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.zoomToFit();
        },
      },
      {
        id: 'timeline.splitActive',
        chord: [
          { code: 'KeyK', meta: true },
          { code: 'KeyK', ctrl: true },
        ],
        priority: 60,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.splitActiveTrackElementAtCurrentTime();
        },
      },
      {
        id: 'timeline.deleteActive',
        chord: [{ code: 'Delete' }, { code: 'Backspace' }],
        priority: 60,
        run: ctx => {
          ctx.nativeEvent.preventDefault();
          deps.deleteActiveElement();
        },
      },
      ...(deps.enableCopyPaste
        ? [
            {
              id: 'timeline.copyElement',
              chord: [
                { code: 'KeyC', meta: true },
                { code: 'KeyC', ctrl: true },
              ],
              priority: 60,
              run: (ctx: { nativeEvent: Event }) => {
                ctx.nativeEvent.preventDefault();
                deps.copyActiveElement();
              },
            },
            {
              id: 'timeline.pasteElement',
              chord: [
                { code: 'KeyV', meta: true },
                { code: 'KeyV', ctrl: true },
              ],
              priority: 60,
              run: (ctx: { nativeEvent: Event }) => {
                ctx.nativeEvent.preventDefault();
                deps.pasteElement();
              },
            },
          ]
        : []),
    ],
  };
}
